import React, { useEffect, useMemo, useState } from "react";
import { FormGroup, Input } from "reactstrap";
import { Container, Row, Col, Toggle, Button } from "rsuite";
import { isNaN } from "lodash";
import cx from "classnames";
import PropTypes from "prop-types";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { getApiErrorMessage } from "../../../utils/apiErrorMessage";
import { useSelector } from "react-redux";
import advancedFormat from "dayjs/plugin/advancedFormat";
import PromotionSummary from "../../../components/promotion-summary/PromotionSummary";
import ConfirmModal from "../../../components/confirm-modal/ConfirmModal";
import { processedDateARIData } from "../../../constants/ARI-utils";
import CloseIcon from "./../../../assets/img/close.svg?react";
import AriList from "./AriList";
import AllotmentMessage from "./AllotmentMessage";
import RoomsARIStatus from "./RoomsARIStatus";
import TaxInfo from "./TaxInfo";
import { updateARI } from "../../../api";
import { UAL_SOURCES } from "../../../constants/ualActions";
import {
  selectRoomtypes,
  selectSelectedRoomtype,
} from "../../../slices/roomtypeSlice";
import { useAriContext } from "../../../contexts/AriContext";
import { usePropertyStore } from "../../../contexts/PropertyStoreContext";
import { isNegotiatedRateplan } from "../../../slices/rateplanSlice";
import { P, usePermission } from "../../../rbac";
import "./right-panel.scss";
import isBetween from "dayjs/plugin/isBetween";

dayjs.extend(advancedFormat);
dayjs.extend(isBetween);

export const hasNoARIChanges = (
  ariData,
  allotment,
  rate,
  isOpened,
  minLeadTime,
  minLos,
  maxLos
) => {
  const toNumberOrZero = (value) =>
    value === null || value === undefined || value === "" ? 0 : Number(value);

  return (
    Number(ariData.inventory) === Number(allotment) &&
    Number(ariData.price) === Number(rate) &&
    ariData.isOpen === isOpened &&
    toNumberOrZero(ariData.minLeadTime) === toNumberOrZero(minLeadTime) &&
    toNumberOrZero(ariData.minLos) === toNumberOrZero(minLos) &&
    toNumberOrZero(ariData.maxLos) === toNumberOrZero(maxLos)
  );
};

const SpecificDateRightPanel = React.forwardRef(
  (
    {
      rateplan,
      isOffline,
      promos,
      derivedRates = [],
      selectedDateCell,
      setSelectedDateCell,
      className = "",
      startDate,
      endDate,
      showingSpecificDateModal,
      setShowingSpecificDateModal,
      ...props
    },
    ref
  ) => {
    const { allowed: canManageAri } = usePermission(P.Ari.Manage);
    const isEditable =
      canManageAri && (isOffline || isNegotiatedRateplan(rateplan));
    const { refetchAriData, ariData: contextAriData } = useAriContext();
    const { date, ariData: selectedAriData } = selectedDateCell ?? {};
    const rooms = useSelector(selectRoomtypes);
    const ariData = useMemo(() => {
      const selectedDateRawData =
        contextAriData?.find(
          (d) =>
            d.date === selectedAriData?.date &&
            d.roomtypeId === selectedAriData?.roomtypeId
        ) || {};

      return processedDateARIData(
        selectedDateRawData,
        selectedAriData?.maxAdultOccupancy
      );
    }, [contextAriData, selectedAriData]);

    const [showModal, setShowModal] = useState(false);
    const [allotment, setAllotment] = useState(ariData.inventory || 0);
    const [rate, setRate] = useState(ariData.price || 0);
    const [isOpened, setIsOpened] = useState(ariData.isOpen);
    const [minLeadTime, setMinLeadTime] = useState(ariData.minLeadTime ?? 0);
    const [minLos, setMinLos] = useState(ariData.minLos ?? 0);
    const [maxLos, setMaxLos] = useState(ariData.maxLos ?? 0);
    const [allotmentError, setAllotmentError] = useState({
      isError: false,
      isBlocker: false,
      message: "",
    });

    const selectedRoomtype = useSelector(selectSelectedRoomtype);
    const { currency, sourceTax, adultPrices } = ariData;

    const { displayName, code } =
      rooms.find(({ id }) => id === selectedRoomtype) ?? {};

    const isOpenLabel = isOpened ? "Open" : "Closed";
    const dayDiff = date?.diff(dayjs(), "day") + 1;
    const isRestricted = dayDiff < ariData.minLeadTime;
    const { selectedProperty } = usePropertyStore();

    const isInvalidNumericField = (value) =>
      isNaN(Number(value)) || Number(value) < 0;

    const verifyAndSetAllotment = (inputValue = 0) => {
      const value = Number(inputValue);
      const isBlocker = isNaN(value) || value < 0;
      const isError = isBlocker || value === 0;
      let message = "";
      if (isBlocker) {
        message = "Enter valid allotment number";
      } else if (isError) {
        message = "Add more rooms";
      }

      setAllotmentError({ isError, isBlocker, message });
      setAllotment(inputValue);
    };

    useEffect(() => {
      verifyAndSetAllotment(ariData.inventory);
      setRate(ariData.price);
      setIsOpened(ariData.isOpen);
      setMinLeadTime(ariData.minLeadTime ?? 0);
      setMinLos(ariData.minLos ?? 0);
      setMaxLos(ariData.maxLos ?? 0);
    }, [ariData]);

    const updateARIForDate = () => {
      const payload = {
        propertyId: ariData?.propertyId || selectedProperty?.propertyId,
        dateRange: {
          startDate: date.format("YYYY-MM-DD"),
          endDate: date.format("YYYY-MM-DD"),
        },
        productUpdates: [
          {
            rateplanId: ariData?.rateplanId || rateplan?.value,
            roomtypeId: ariData?.roomtypeId || selectedRoomtype,
            updates: [
              {
                ...ariData,
                // date should have a fallback because ARI response may be missing ARI of few dates
                date: ariData?.date || date.format("YYYY-MM-DD"),
                isOpen: Boolean(isOpened),
                inventory: Number(allotment),
                price:
                  typeof rate === "string" ? rate : Number(rate.toFixed(2)),
                minLeadTime: Number(minLeadTime) || 0,
                minLos: Number(minLos) || 0,
                maxLos: Number(maxLos) || 0,
              },
            ],
          },
        ],
      };

      setShowModal(false);
      updateARI(payload, UAL_SOURCES.ARI_CALENDAR_INDIVIDUAL)
        .then(() => {
          setAllotmentError({
            isError: false,
            isBlocker: false,
            message: "ARI updated successfully.",
          });
          setTimeout(() => {
            refetchAriData({
              propertyId: selectedProperty?.propertyId,
              rateplanId: rateplan?.value,
              rooms,
              startDate,
              endDate,
              includeNumberOfRoomsSold: true,
              roomtypeIdsToRefresh: [payload.productUpdates[0].roomtypeId],
            });
            toast.success("ARI updated successfully.");
          }, 2000);
        })
        .catch((error) => {
          toast.error(getApiErrorMessage(error, "Failed to save updates."));
        });
    };

    const applicablePromos = promos?.filter(({ roomtypeIds, ruleJson }) => {
      const rule = JSON.parse(ruleJson);
      const { stayVO = {} } = rule?.ruleVO || {};
      const blackOutDateRanges = stayVO?.blackOutDateRanges || [];
      // Skip blackout date check if blackOutDateRanges is empty
      if (blackOutDateRanges.length === 0) {
        return roomtypeIds?.split(",").includes(String(selectedRoomtype || ""));
      }

      const selectedDate = dayjs(date);
      // Check if the selected date falls within any blackout date range
      const isBlackoutDate = blackOutDateRanges.some(
        ({ blackOutStartDate, blackOutEndDate, blackOutStayThruEnabled }) => {
          if (!blackOutStayThruEnabled) return false;

          const startDate = dayjs(blackOutStartDate);
          const endDate = dayjs(blackOutEndDate);

          return selectedDate.isBetween(startDate, endDate, "day", "[]"); // Inclusive of start and end dates
        }
      );

      // Exclude promos for blackout dates
      return (
        !isBlackoutDate &&
        roomtypeIds?.split(",").includes(String(selectedRoomtype || ""))
      );
    });

    const applicableDerivedRates = derivedRates?.filter((dr) => {
      if (
        dr.roomTypeIds?.length > 0 &&
        !dr.roomTypeIds?.includes(selectedRoomtype)
      )
        return false;
      const selectedDate = dayjs(date);
      const stayDates = dr.restrictionConfig?.stayDates || [];
      if (
        stayDates.length > 0 &&
        !stayDates.some(({ startDate: sd, endDate: ed }) =>
          selectedDate.isBetween(dayjs(sd), dayjs(ed), "day", "[]")
        )
      ) {
        return false;
      }
      const blackOutDates = dr.restrictionConfig?.blackOutDates || [];
      if (blackOutDates.length > 0) {
        const isBlackout = blackOutDates.some(
          ({ startDate: sd, endDate: ed, allowStayThrough }) =>
            !allowStayThrough &&
            selectedDate.isBetween(dayjs(sd), dayjs(ed), "day", "[]")
        );
        if (isBlackout) return false;
      }
      return true;
    });

    return (
      <Container
        className={cx("right-panel-container right-panel-wrapper", className)}
        ref={ref}
        {...props}
      >
        <Row>
          <Col xs={22} className="right-panel-header-one">
            {date?.format("dddd, Do MMM")}
          </Col>
          <Col
            xs={2}
            className="date-clear"
            onClick={() => {
              setShowingSpecificDateModal(false);
              // to ensure that form gets hidden to avoid the validation error
              setTimeout(() => setSelectedDateCell(), 300);
            }}
          >
            <CloseIcon />
          </Col>
        </Row>
        <Row className="right-panel-header-two">
          <Col className="header-rateplan-name">
            <span>{displayName}</span>
          </Col>
          <Col>
            <span>{` | ${code}`}</span>
          </Col>
        </Row>

        <PromotionSummary
          promos={applicablePromos} // when date is selected from calendar, rateplan details should not be shown.
          date={date}
          derivedRates={applicableDerivedRates}
        />
        <RoomsARIStatus
          roomsSold={ariData.roomsSold}
          allotment={ariData.inventory}
        />

        {isEditable ? (
          <div className="date-update-form">
            <AllotmentMessage allotmentError={allotmentError} />
            <FormGroup>
              <Row
                className={cx({
                  "error-input-row": allotmentError.isError,
                })}
              >
                <Col xs={12} className="row-label">
                  Allotment
                </Col>
                <Col xs={12}>
                  <Input
                    type="input"
                    name="allotment"
                    id="allotment"
                    disabled={isRestricted}
                    value={allotment ?? ""}
                    onChange={(event) => {
                      verifyAndSetAllotment(event?.target?.value);
                    }}
                  />
                </Col>
              </Row>
              <Row
                className={cx({
                  "error-input-row": isNaN(Number(rate)) || Number(rate) <= 0,
                })}
              >
                <Col xs={12} className="row-label">
                  {`Rate (${currency ?? rateplan?.data?.currencyCode ?? ""})`}
                </Col>
                <Col xs={12}>
                  <Input
                    type="input"
                    name="rate"
                    id="rate"
                    disabled={isRestricted}
                    value={rate ?? ""}
                    onChange={(event) => {
                      setRate(event?.target?.value);
                    }}
                  />
                </Col>
              </Row>
              <TaxInfo
                currency={currency ?? rateplan?.data?.currencyCode ?? ""}
                sourceTax={sourceTax}
                adultPrices={adultPrices}
              />
              <Row>
                <Col xs={12} className="row-label">
                  Availability
                </Col>
                <Col xs={12}>
                  <Toggle
                    size="sm"
                    disabled={isRestricted}
                    checked={isOpened}
                    onChange={() => setIsOpened(!isOpened)}
                    locale={{ on: "Open", off: "Closed" }}
                  >
                    {isOpenLabel}
                  </Toggle>
                </Col>
              </Row>
              <Row
                className={cx({
                  "error-input-row": isInvalidNumericField(minLeadTime),
                })}
              >
                <Col xs={12} className="row-label">
                  Min Lead Time
                </Col>
                <Col xs={12}>
                  <Input
                    type="input"
                    name="minLeadTime"
                    id="minLeadTime"
                    disabled={isRestricted}
                    value={minLeadTime ?? ""}
                    onChange={(event) => {
                      setMinLeadTime(event?.target?.value);
                    }}
                  />
                </Col>
              </Row>
              <Row
                className={cx({
                  "error-input-row":
                    isInvalidNumericField(minLos) ||
                    isInvalidNumericField(maxLos),
                })}
              >
                <Col xs={12} className="row-label">
                  Min | Max Length of Stay
                </Col>
                <Col xs={12}>
                  <div className="los-inputs-container">
                    <Input
                      type="input"
                      name="minLos"
                      id="minLos"
                      disabled={isRestricted}
                      value={minLos ?? ""}
                      onChange={(event) => {
                        setMinLos(event?.target?.value);
                      }}
                    />
                    <span className="los-separator">|</span>
                    <Input
                      type="input"
                      name="maxLos"
                      id="maxLos"
                      disabled={isRestricted}
                      value={maxLos ?? ""}
                      onChange={(event) => {
                        setMaxLos(event?.target?.value);
                      }}
                    />
                  </div>
                </Col>
              </Row>
            </FormGroup>
          </div>
        ) : null}

        {!isEditable && <AriList data={ariData} currency={currency} />}

        {isEditable ? (
          <Row className="form-buttons">
            <Button
              className="cancel-btn"
              onClick={() => setSelectedDateCell()}
            >
              Cancel
            </Button>
            <Button
              className="save-btn"
              disabled={
                isRestricted ||
                allotmentError.isBlocker ||
                isNaN(Number(rate)) ||
                Number(rate) <= 0 ||
                isInvalidNumericField(minLeadTime) ||
                isInvalidNumericField(minLos) ||
                isInvalidNumericField(maxLos) ||
                hasNoARIChanges(
                  ariData,
                  allotment,
                  rate,
                  isOpened,
                  minLeadTime,
                  minLos,
                  maxLos
                )
              }
              onClick={() => setShowModal(true)}
            >
              Save changes
            </Button>
          </Row>
        ) : null}
        <ConfirmModal
          show={showModal}
          onClose={() => setShowModal(false)}
          onConfirm={updateARIForDate}
          message={`You’re about to update the ARI (availability, rate, inventary) for ${date?.format("ddd Do MMMM")}. Are you happy to continue?`}
          title="Please confirm update"
        />
      </Container>
    );
  }
);

SpecificDateRightPanel.propTypes = {
  selectedDateCell: PropTypes.shape({
    ariData: PropTypes.shape({
      date: PropTypes.string,
      currency: PropTypes.string,
      inventory: PropTypes.number,
      roomsSold: PropTypes.number,
      isOpen: PropTypes.bool,
      price: PropTypes.number,
      sourceTax: PropTypes.string,
      adultPrices: PropTypes.arrayOf(PropTypes.number),
      propertyId: PropTypes.number,
      rateplanId: PropTypes.number,
      roomtypeId: PropTypes.number,
      minLeadTime: PropTypes.number,
    }).isRequired,
    date: PropTypes.instanceOf(dayjs).isRequired,
  }).isRequired,
  promos: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      ruleName: PropTypes.string,
      ruleJson: PropTypes.string,
      bookingEnabled: PropTypes.bool,
      disabled: PropTypes.bool,
      roomtypeIds: PropTypes.string,
    })
  ),
  rateplan: PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.number,
    data: PropTypes.shape({
      code: PropTypes.string,
      currencyCode: PropTypes.string,
    }),
  }),
  isOffline: PropTypes.bool,
  setSelectedDateCell: PropTypes.func.isRequired,
  className: PropTypes.string,
  startDate: PropTypes.string,
  endDate: PropTypes.string,
  showingSpecificDateModal: PropTypes.bool,
  setShowingSpecificDateModal: PropTypes.func.isRequired,
};

SpecificDateRightPanel.displayName = "SpecificDateRightPanel";

export default SpecificDateRightPanel;
